import type {FunctionComponent, SyntheticEvent} from 'react'
import React from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {objectToQueryString} from '~/utils/commons'
import type {Product, RootAndListContext, RootLoaderResponse} from '~/types'
import {longWordsLanguages, getTranslation} from '~/localization/translation'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {AmazonLocalizedLogo} from '~/icons/icon-amazon-logo-by-locale'
import {getDomainConfig} from '~/domains'
import {MadeInByLocaleIcon} from '~/icons/made-in-by-locale-icon'
import {grantAllConsent} from '~/utils/consent-manager.client'
import ProductCardBadge from '../badges/card-placement-badge'
import {MagaLoclaedBrandBadge} from '../badges/localed-brand-badge'
import SaleEventBadge from '../badges/saleEventBadge'

export interface ProductSponsoredCardProps {
  index: number
  product: Product
}

export const ProductSponsoredEUCard: FunctionComponent<ProductSponsoredCardProps> = ({product, index = 0}) => {
  const {image, name, brand = '', affiliateLink, asin, isSellerProduct, price, forcedPosition, isLocaleBrand} = product
  const {locale, featureFlags, language, trackAnalytics, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const {
    dataResponse: {productsCategory},
    clickId,
    setShowCookieConsentGdpr,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()

  const {domainType, styles: stylesBydomain} = getDomainConfig(domain, locale)
  const isMagaDomainType = domainType === 'maga'
  const shouldTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const showVariantsIndication =
    featureFlags?.showVariantsIndication && product?.variationDetails && product?.variationDetails.length > 0

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const reportWidgetClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickout({
    product,
    index,
    locale,
    listName: 'sponsored-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })

  const [cardRef] = useReportItemSeen(
    {
      item_list_id: productsCategory?.slug,
      item_name: name,
      item_source: 'widget',
      partner_item_brand: brand,
      partner_item_id: asin,
      affiliation: affiliateLink.type,
      is_dws: isSellerProduct,
      price: price?.amount,
      currency: price?.currency,
      click_element_name: 'sponsored-product',
      index,
      variation: JSON.stringify({
        hasVariantIndication: showVariantsIndication,
        forcedPosition,
      }),
    },
    {},
  )

  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()

      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'sponsored-product-card',
      widget_place: 'top',
      variation: JSON.stringify({
        forcedPosition,
      }),
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  return (
    <>
      <div
        data-testid={`product-card-${index}`}
        ref={cardRef}
        className="border-gray relative z-20 flex h-full min-h-52 w-full flex-col overflow-hidden rounded-2xl border bg-white shadow-md transition-all duration-200 ease-in-out"
      >
        {isLocaleBrand && (
          <MadeInByLocaleIcon
            className="absolute right-3 top-2 drop-shadow-[2px_4px_6px_rgba(0,0,0,0.4)] tablet:top-1"
            locale={locale}
          />
        )}
        <ProductCardBadge
          index={index}
          badge="sponsored"
          coupon={undefined}
          shouldRemoveLogoFromBadge={true}
          className="absolute left-6 top-0 overflow-hidden rounded-b-lg rounded-t-none"
        />
        <div className="px-10 pb-5 pt-8 tablet:px-2">
          <div className="mt-2 flex flex-row gap-6 tablet:flex-col tablet:gap-1 tablet:px-4">
            {/* -------------------------------------- IMAGE DESKTOP------------------------------- */}
            <div className="relative flex size-44 shrink-0 flex-col items-center justify-center tablet:hidden tablet:size-auto tablet:flex-row tablet:justify-evenly tablet:gap-5">
              <img
                src={image}
                loading={index > 2 ? 'lazy' : 'eager'}
                alt={name?.slice(0, 10)}
                className="relative size-44 shrink-0 grow-0 object-scale-down mix-blend-multiply tablet:size-32"
              />
              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
                className="absolute inset-0"
              />
            </div>
            {/* -------------------------------------- INFO------------------------------- */}
            <div className="flex flex-col justify-center gap-3">
              <div className="flex flex-row tablet:mt-2">
                <div className="flex flex-col justify-center gap-3">
                  <div className="flex flex-row items-center gap-4 *:-mb-1 tablet:flex-col tablet:items-start">
                    <span className="relative text-base font-bold text-gray-900">
                      {brand}
                      <a
                        href={redirectUrl}
                        onClick={handleCardClick}
                        aria-label={`view ${brand}`}
                        target="_blank"
                        rel="sponsored noreferrer"
                        className="absolute inset-0"
                      />
                    </span>
                    {isLocaleBrand && <MagaLoclaedBrandBadge className="[&>div>svg>g>g>.border-flag]:!stroke-none" />}
                  </div>
                  {/* ------------------------------------- Name ------------------------------- */}

                  <h2 className="relative line-clamp-2 overflow-hidden text-base font-light leading-6 no-underline">
                    {name}
                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                      className="absolute inset-0"
                    />
                  </h2>
                </div>
                {/* -------------------------------------- IMAGE MOBILE------------------------------- */}

                <div className="relative hidden size-32 shrink-0 flex-col items-center justify-center tablet:flex tablet:size-auto tablet:flex-row tablet:justify-evenly tablet:gap-5">
                  <img
                    src={image}
                    loading={index > 2 ? 'lazy' : 'eager'}
                    alt={name?.slice(0, 10)}
                    className="srelative ize-32 shrink-0 grow-0 object-scale-down mix-blend-multiply tablet:size-32"
                  />
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    className="absolute inset-0"
                  />
                </div>
              </div>
            </div>
            <div
              className={clsx(
                'ml-auto flex flex-col desktop:justify-center tablet:mx-auto tablet:w-full',
                isMagaDomainType ? 'w-71 min-w-71' : 'w-56 min-w-56',
              )}
            >
              {/* -------------------------------------- Rating/Reviews Desktop------------------------------- */}
              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
              >
                <AmazonLocalizedLogo locale={locale?.toLowerCase()} className="mx-auto tablet:hidden" />
              </a>
              {/* -------------------------------------------CTA---------------------------------------- */}
              <div className="flex w-full flex-col items-center justify-center tablet:mt-5 tablet:flex-row tablet:gap-4">
                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                >
                  <AmazonLocalizedLogo locale={locale?.toLowerCase()} className="hidden shrink-0 tablet:block" />
                </a>

                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                  className={clsx(
                    'mt-6 flex h-10 w-full cursor-pointer items-center justify-center rounded-3xl text-center capitalize tablet:mt-0',
                    longWordsLanguages.includes(language) ? 'px-1 text-base desktop:text-sm' : 'text-base',
                    stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
                    'listpage_button_primary',
                  )}
                >
                  {translations.ProductCard.additionalCtaText}
                </a>
              </div>
              {!!product?.discount && (
                <SaleEventBadge
                  redirectUrl={redirectUrl}
                  handleCardClick={handleCardClick}
                  brand={brand}
                  index={index}
                  placement="eu-card"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
